import i18next from 'i18next';
import moment from 'moment-timezone';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { formatTimeWithLanguageCode } from '@commons/utils/date';
import { TRANSFER_STATUS_LABELS } from '@stocks/StocksInventories/Transfers/utils/constants';

export const getSupplierProductTransfertsExportColumns = ({
  storeName,
  hasTransferDeliveryTime,
  userLanguageCode,
}) => {
  const columns = [
    {
      propertyKey: 'reference',
      name: i18next.t('STOCKS.TRANSFERS.CONTENT_EXPORT_REFERENCE'),
    },
    {
      propertyKey: 'recipient',
      name: i18next.t('STOCKS.TRANSFERS.CONTENT_EXPORT_RECIPIENT', { storeName }),
    },
    {
      propertyKey: 'sender',
      name: i18next.t('STOCKS.TRANSFERS.CONTENT_EXPORT_SENDER', { storeName }),
    },
    {
      propertyKey: 'sentAt',
      name: i18next.t('GENERAL.DATE'),
      transform: (date) => moment(date).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR),
    },
  ];

  if (hasTransferDeliveryTime) {
    columns.push({
      propertyKey: 'deliveryTime',
      name: i18next.t('STOCKS.TRANSFERS.DELIVERY_TIME'),
      transform: (time) => {
        if (!time) {
          return null;
        }

        return formatTimeWithLanguageCode(time, userLanguageCode);
      },
    });
  }

  columns.push(
    {
      propertyKey: 'status',
      name: i18next.t('STOCKS.TRANSFERS.CONTENT_EXPORT_STATUS'),
      transform: (status) => i18next.t(TRANSFER_STATUS_LABELS[status]),
    },
    {
      propertyKey: 'subCategory',
      name: i18next.t('GENERAL.SUB_CATEGORY'),
      transform: (subCategory) => subCategory || i18next.t('GENERAL.NONE_VALUE'),
    },
    {
      propertyKey: 'sku',
      name: i18next.t('GENERAL.SKU'),
    },
    {
      propertyKey: 'supplierProductName',
      name: i18next.t('GENERAL.SUPPLIER_PRODUCT'),
    },
    {
      propertyKey: 'supplierName',
      name: i18next.t('GENERAL.SUPPLIER'),
    },
    {
      propertyKey: 'packagingName',
      name: i18next.t('GENERAL.PACKAGING'),
    },
    {
      type: 'currency',
      propertyKey: 'price',
      name: i18next.t('STOCKS.TRANSFERS.EXPORT_PRICE_PER_PACKAGING'),
    },
    {
      propertyKey: 'transferNoteQuantity',
      name: i18next.t('STOCKS.TRANSFERS.EXPORT_TRANSFER_NOTE_QUANTITY'),
    },
    {
      propertyKey: 'deliveryNoteQuantity',
      name: i18next.t('ORDERS.ORDERS.LIST_LABEL_QUANTITY_BDL'),
    },
    {
      propertyKey: 'receivedQuantity',
      name: i18next.t('ORDERS.ORDERS.LIST_LABEL_QUANTITY_RECEIVED'),
    },
    {
      type: 'currency',
      propertyKey: 'totalDeliveryNote',
      name: i18next.t('ORDERS.ORDERS.LIST_LABEL_TOTAL_BDL'),
    },
    {
      type: 'currency',
      propertyKey: 'totalReceived',
      name: i18next.t('ORDERS.ORDERS.LIST_LABEL_TOTAL_RECEIVED'),
    },
    {
      type: 'percentage',
      propertyKey: 'vatRate',
      name: i18next.t('GENERAL.VAT_RATE'),
    },
    {
      propertyKey: 'currency',
      name: i18next.t('GENERAL.CURRENCY'),
    },
  );

  return columns;
};

export const getRecipeTransfertsExportColumns = ({
  storeName,
  hasTransferDeliveryTime,
  userLanguageCode,
}) => {
  const columns = [
    {
      propertyKey: 'reference',
      name: i18next.t('STOCKS.TRANSFERS.CONTENT_EXPORT_REFERENCE'),
    },
    {
      propertyKey: 'recipient',
      name: i18next.t('STOCKS.TRANSFERS.CONTENT_EXPORT_RECIPIENT', { storeName }),
    },
    {
      propertyKey: 'sender',
      name: i18next.t('STOCKS.TRANSFERS.CONTENT_EXPORT_SENDER', { storeName }),
    },
    {
      propertyKey: 'sentAt',
      name: i18next.t('GENERAL.DATE'),
      transform: (date) => moment(date).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR),
    },
  ];

  if (hasTransferDeliveryTime) {
    columns.push({
      propertyKey: 'deliveryTime',
      name: i18next.t('STOCKS.TRANSFERS.DELIVERY_TIME'),
      transform: (time) => {
        if (!time) {
          return null;
        }

        return formatTimeWithLanguageCode(time, userLanguageCode);
      },
    });
  }

  columns.push(
    {
      propertyKey: 'status',
      name: i18next.t('STOCKS.TRANSFERS.CONTENT_EXPORT_STATUS'),
      transform: (status) => i18next.t(TRANSFER_STATUS_LABELS[status]),
    },
    {
      propertyKey: 'category',
      name: i18next.t('GENERAL.CATEGORY'),
      transform: (category) => category || i18next.t('GENERAL.NONE_VALUE'),
    },
    {
      propertyKey: 'recipeName',
      name: i18next.t('GENERAL.RECIPE'),
    },
    {
      propertyKey: 'unit',
      name: i18next.t('GENERAL.UNIT'),
    },
    {
      type: 'currency',
      propertyKey: 'price',
      name: i18next.t('STOCKS.TRANSFERS.EXPORT_PRICE_PER_PACKAGING'),
    },
    {
      propertyKey: 'transferNoteQuantity',
      name: i18next.t('STOCKS.TRANSFERS.EXPORT_TRANSFER_NOTE_QUANTITY'),
    },
    {
      propertyKey: 'deliveryNoteQuantity',
      name: i18next.t('ORDERS.ORDERS.LIST_LABEL_QUANTITY_BDL'),
    },
    {
      type: 'currency',
      propertyKey: 'receivedQuantity',
      name: i18next.t('ORDERS.ORDERS.LIST_LABEL_QUANTITY_RECEIVED'),
    },
    {
      type: 'currency',
      propertyKey: 'totalDeliveryNote',
      name: i18next.t('ORDERS.ORDERS.LIST_LABEL_TOTAL_BDL'),
    },
    {
      propertyKey: 'totalReceived',
      name: i18next.t('ORDERS.ORDERS.LIST_LABEL_TOTAL_RECEIVED'),
    },
    {
      propertyKey: 'currency',
      name: i18next.t('GENERAL.CURRENCY'),
    },
  );

  return columns;
};
